import React, { useState } from 'react';
import { 
  X, 
  DollarSign, 
  Loader2, 
  CreditCard, 
  Zap, 
  Shield,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useBilling } from '../hooks/useBilling';
import './Modal.css';

interface AddFundsModalProps {
  onClose: () => void;
}

const AddFundsModal: React.FC<AddFundsModalProps> = ({ onClose }) => {
  const { addFunds, balance } = useBilling();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

  const presetAmounts = [
    { value: 20, label: '$20', description: '~1,000 contacts' },
    { value: 50, label: '$50', description: '~2,500 contacts' },
    { value: 100, label: '$100', description: '~5,000 contacts' },
    { value: 200, label: '$200', description: '~10,000 contacts' }
  ];

  const handlePresetClick = (preset: number) => {
    setAmount(String(preset));
    setSelectedPreset(preset);
    setError('');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setSelectedPreset(null);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount) || numAmount < 1) {
      setError('Minimum amount is $1.00');
      return;
    }
    
    if (numAmount > 1000) {
      setError('Maximum amount is $1,000.00');
      return;
    }

    setLoading(true);
    const success = await addFunds(numAmount);
    if (success) {
      onClose();
    }
    setLoading(false);
  };

  const numAmount = parseFloat(amount) || 0;
  const estimatedContacts = Math.floor(numAmount / 0.02);
  const hasPaymentMethod = balance?.has_payment_method;

  return (
    <div className="modal-overlay premium" onClick={onClose}>
      <div className="modal-content add-funds-modal premium" onClick={e => e.stopPropagation()}>
        <div className="modal-header premium">
          <div className="header-content">
            <div className="header-icon">
              <DollarSign size={24} />
            </div>
            <div>
              <h2>Add Funds to Your Account</h2>
              <p className="header-subtitle">Fund your account to continue unlocking contacts</p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close premium">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body premium">
            {!hasPaymentMethod && (
              <div className="warning-banner premium">
                <AlertCircle size={20} />
                <div>
                  <strong>No payment method on file</strong>
                  <p>You'll need to add a payment method first</p>
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="form-section">
              <label className="form-label premium">Amount to Add</label>
              <div className="amount-input-wrapper premium">
                <div className="currency-symbol">$</div>
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  className={`amount-input premium ${error ? 'error' : ''}`}
                  autoFocus
                />
                <div className="input-help">USD</div>
              </div>
              {error && (
                <div className="error-message premium">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}
              {numAmount > 0 && (
                <div className="amount-preview">
                  <Zap size={14} />
                  Approximately {estimatedContacts.toLocaleString()} contacts
                </div>
              )}
            </div>

            {/* Preset Amounts */}
            <div className="form-section">
              <label className="form-label premium">Quick Select</label>
              <div className="preset-grid premium">
                {presetAmounts.map(preset => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`preset-card ${selectedPreset === preset.value ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset.value)}
                  >
                    <div className="preset-amount">{preset.label}</div>
                    <div className="preset-description">{preset.description}</div>
                    {selectedPreset === preset.value && (
                      <CheckCircle size={16} className="preset-check" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="payment-summary premium">
              <div className="summary-header">
                <Shield size={20} />
                <span>Payment Summary</span>
              </div>
              <div className="summary-content">
                <div className="summary-row">
                  <span>Amount to add</span>
                  <span className="summary-value">${numAmount.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Processing fee</span>
                  <span className="summary-value">$0.00</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-row total">
                  <span>Total charge</span>
                  <span className="summary-value">${numAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="summary-footer">
                <CreditCard size={14} />
                <span>Secure payment via Stripe</span>
              </div>
            </div>
          </div>

          <div className="modal-footer premium">
            <button type="button" onClick={onClose} className="btn-secondary premium">
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading || numAmount < 1 || !hasPaymentMethod} 
              className="btn-primary premium"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Add ${numAmount > 0 ? numAmount.toFixed(2) : '0.00'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFundsModal;